import {
    Directive,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StateMatchType } from './state-match-type';
import { RouterService } from '@core/state/router/router.service';
import { IAppRouterState } from '@core/state/router/router-state';

@Directive({
    selector: '[payStateRef]'
})
export class StateRefDirective implements OnInit, OnDestroy {
    @Input()
    state: string;

    @Input()
    activeClass: string;

    // Matching criteria for the state name, defaults to an exact match
    // useful when u have child routes and you want the particular element to be active for all child routes as well
    @Input()
    matchType?: StateMatchType = StateMatchType.EXACT;

    /**
     * Exclude states (path) for matching. If you wanted to exclude a particular sub-path route tree from matching
     * Eg. 'hiring' is active state but you wanted to exclude 'hiring.list' sub-path from matching but all other under 'hiring'
     * would match. so if exclude path = ['hiring.list'] for matchType = startsWith
     * hiring = match
     * hiring.search = match
     * hiring.list = no match
     */
    @Input()
    excludePath?: string[];

    private routerState: IAppRouterState;
    private nativeElement: any;
    private destroyed$: Subject<void> = new Subject();

    constructor(
        private routerService: RouterService,
        private element: ElementRef,
        private renderer: Renderer2
    ) {
        this.nativeElement = this.element.nativeElement;
    }

    ngOnInit() {
        this.routerService
            .getCurrentState()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((state: IAppRouterState) => {
                this.routerState = state;

                if (this.routerState && this.routerState.name) {
                    this.setActiveClass();
                }
            });
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    private setActiveClass() {
        const classList: DOMTokenList = this.nativeElement.classList;
        const isClassPresent: boolean = classList.contains(this.activeClass);

        let isMatching: boolean = false;

        switch (this.matchType) {
            case StateMatchType.EXACT:
                isMatching = this.state === this.routerState.name;
                break;

            case StateMatchType.STARTS_WITH:
                isMatching = this.routerState.name.indexOf(this.state) === 0;
                break;

            case StateMatchType.CONTAINS:
                isMatching = this.routerState.name.indexOf(this.state) >= 0;
                break;

            default:
        }

        if (isMatching && this.excludePath?.length) {
            isMatching =
                this.excludePath.filter(
                    (path: string) => this.routerState.name.indexOf(path) === 0
                ).length === 0;
        }

        if (isMatching) {
            if (!isClassPresent) {
                this.renderer.addClass(this.nativeElement, this.activeClass);
            }
        } else {
            if (isClassPresent) {
                this.renderer.removeClass(this.nativeElement, this.activeClass);
            }
        }
    }
}
